import React, { useState } from "react";
import { ChamberMemberProSenderDomainStatusEnum } from "../../../../../graphql/types";
import {
  FieldLabel,
  SettingsModal,
  TextInput,
  settingsPrimaryButtonClassName,
  settingsSecondaryButtonClassName,
} from "../common";
import { ChamberSettingsFormState } from "../settingsData";

type EmailSettings = ChamberSettingsFormState["email"];

type EmailDomainSettingsSectionProps = {
  settings: EmailSettings;
  onStartSenderDomainVerification: (values: {
    domain: string;
    senderEmail: string;
  }) => Promise<void>;
  onRefreshSenderDomainStatus: () => Promise<void>;
  onRemoveSenderDomain: () => Promise<void>;
  onSendSenderDomainTestEmail: (recipientEmail: string) => Promise<void>;
  senderDomainActionLoading: boolean;
  senderDomainRefreshLoading: boolean;
  senderDomainRemoveLoading: boolean;
  senderDomainTestEmailLoading: boolean;
  senderDomainError: string | null;
  hasUnsavedSettingsChanges: boolean;
};

const EmailDomainSettingsSection = ({
  settings,
  onStartSenderDomainVerification,
  onRefreshSenderDomainStatus,
  onRemoveSenderDomain,
  onSendSenderDomainTestEmail,
  senderDomainActionLoading,
  senderDomainRefreshLoading,
  senderDomainRemoveLoading,
  senderDomainTestEmailLoading,
  senderDomainError,
  hasUnsavedSettingsChanges,
}: EmailDomainSettingsSectionProps) => {
  const [senderDomainModalOpen, setSenderDomainModalOpen] = useState(false);
  const [senderDomainInput, setSenderDomainInput] = useState("");
  const [senderDomainEmailInput, setSenderDomainEmailInput] = useState("");
  const [showSenderDomainDnsRecords, setShowSenderDomainDnsRecords] =
    useState(false);
  const [testEmailModalOpen, setTestEmailModalOpen] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState("");

  const { senderEmail, senderDomain } = settings;
  const senderDomainConfigured =
    senderDomain.status !== ChamberMemberProSenderDomainStatusEnum.NotConfigured;
  const senderDomainVerified =
    senderDomain.status === ChamberMemberProSenderDomainStatusEnum.Verified;
  const senderDomainConnected = senderDomainVerified && Boolean(senderDomain.domain);
  const senderDomainPending =
    senderDomain.status ===
    ChamberMemberProSenderDomainStatusEnum.PendingVerification;
  const senderDomainActionDisabled =
    senderDomainActionLoading || hasUnsavedSettingsChanges;
  const senderDomainRefreshDisabled =
    senderDomainRefreshLoading || hasUnsavedSettingsChanges;
  const senderDomainRemoveDisabled =
    senderDomainRemoveLoading || hasUnsavedSettingsChanges;
  const senderDomainTestEmailDisabled =
    senderDomainTestEmailLoading ||
    hasUnsavedSettingsChanges ||
    !senderDomainConnected;
  const shouldShowSenderDomainDnsRecords =
    senderDomain.dnsRecords.length > 0 &&
    (!senderDomainVerified || showSenderDomainDnsRecords);
  const senderDomainStatusLabel = senderDomainConnected
    ? "Domain is connected"
    : senderDomain.domain
    ? senderDomainPending
      ? "Domain is pending verification"
      : "Domain is not connected properly"
    : "Domain is not connected";
  const senderDomainStatusDescription = senderDomainConnected
    ? "Emails can send from this chamber domain."
    : senderDomain.domain
    ? senderDomainPending
      ? "DNS records are saved, but the domain is still waiting for verification."
      : "DNS records need attention before this domain can send email."
    : "Set up a domain to send emails from chamber addresses.";
  const senderDomainStatusClassName = senderDomainConnected
    ? "bg-emerald-700"
    : senderDomainPending
    ? "bg-amber-500"
    : "bg-red-600";

  const openSenderDomainSetup = () => {
    setSenderDomainInput(senderDomain.domain || "");
    setSenderDomainEmailInput(senderDomain.senderEmail || senderEmail || "");
    setShowSenderDomainDnsRecords(false);
    setSenderDomainModalOpen(true);
  };

  const handleSenderDomainSetup = async () => {
    await onStartSenderDomainVerification({
      domain: senderDomainInput,
      senderEmail: senderDomainEmailInput,
    });
    setSenderDomainModalOpen(false);
  };

  const handleSendTestEmail = async () => {
    await onSendSenderDomainTestEmail(testEmailRecipient);
    setTestEmailModalOpen(false);
    setTestEmailRecipient("");
  };

  const copyText = (value: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value);
    }
  };

  return (
    <section className="rounded-lg border border-black bg-white px-6 py-6 shadow-none">
      <div>
        <h2 className="whitespace-pre-wrap text-xl font-bold text-black">
          Email Domain Set Up (Done During Onboarding  Session)
        </h2>
        <p className="mt-1 text-base leading-6 text-black">
          Connect your chambers domain to send emails from your chamber's email addresses.
        </p>
      </div>

      <div className="mt-7 space-y-5">
        <div className="flex min-h-[150px] flex-col gap-4 rounded-lg bg-white px-0 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex w-fit items-center justify-center rounded-full px-6 py-3 text-base font-bold text-white ${senderDomainStatusClassName}`}
              >
                {senderDomainStatusLabel}
              </span>
              {senderDomain.domain && (
                <p className="break-all text-xl font-bold text-black">
                  {senderDomain.domain}
                </p>
              )}
            </div>
            <p className="mt-3 text-base leading-6 text-black">
              {senderDomainStatusDescription}
            </p>
            {senderDomain.failureReason && (
              <p className="mt-2 text-sm font-semibold text-red-700">
                {senderDomain.failureReason}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap gap-3 lg:justify-end">
            <button
              type="button"
              onClick={openSenderDomainSetup}
              disabled={senderDomainActionDisabled}
              className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-cpaGold px-8 text-sm font-bold text-white transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              {senderDomainActionLoading
                ? "Starting..."
                : senderDomainConfigured
                ? "Update Setup"
                : "Set Up"}
            </button>
            {senderDomainConfigured && (
              <button
                type="button"
                onClick={onRefreshSenderDomainStatus}
                disabled={senderDomainRefreshDisabled}
                className={settingsSecondaryButtonClassName}
              >
                {senderDomainRefreshLoading ? "Checking..." : "Check Again"}
              </button>
            )}
            {senderDomainConnected && (
              <button
                type="button"
                onClick={() => setTestEmailModalOpen(true)}
                disabled={senderDomainTestEmailDisabled}
                className={settingsSecondaryButtonClassName}
              >
                {senderDomainTestEmailLoading ? "Sending..." : "Send Test"}
              </button>
            )}
            {senderDomainConfigured && (
              <button
                type="button"
                onClick={onRemoveSenderDomain}
                disabled={senderDomainRemoveDisabled}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {senderDomainRemoveLoading ? "Removing..." : "Remove Setup"}
              </button>
            )}
          </div>

          {senderDomainError && (
            <div className="w-full rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 lg:col-span-2">
              {senderDomainError}
            </div>
          )}

          {hasUnsavedSettingsChanges && (
            <div className="w-full rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 lg:col-span-2">
              Save or reset your changes before updating email setup.
            </div>
          )}
        </div>

        <div className="flex min-h-[120px] flex-col gap-4 rounded-lg bg-white px-0 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-lg font-bold text-black">
              Used to connect domain during onboarding set up
            </p>
            <p className="mt-2 text-base text-black">
              DNS records used for domain set up.
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setShowSenderDomainDnsRecords(!showSenderDomainDnsRecords)
            }
            className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-cpaGold px-8 text-sm font-bold text-white transition hover:bg-amber-500 md:w-[220px]"
          >
            {showSenderDomainDnsRecords ? "Hide Records" : "Show Records"}
          </button>
        </div>

        {shouldShowSenderDomainDnsRecords && (
          <div className="space-y-3 rounded-lg bg-white py-4">
            {senderDomain.dnsRecords.map((record) => (
              <div
                key={`${record.type}-${record.name}-${record.value}`}
                className="rounded-lg bg-white p-4"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
                  <div className="w-20 shrink-0">
                    <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">
                      {record.type}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Name
                      </p>
                      <p className="mt-1 break-all text-sm font-medium text-gray-900">
                        {record.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Value
                      </p>
                      <p className="mt-1 break-all text-sm text-gray-700">
                        {record.value}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyText(record.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 lg:w-auto"
                  >
                    Copy Value
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {senderDomainModalOpen && (
        <SettingsModal
          title="Set up chamber email"
          description="Use an email address your members already recognize."
          onClose={() => setSenderDomainModalOpen(false)}
        >
          <div className="space-y-4">
            <FieldLabel
              label="Chamber website domain"
              helper="Example: northbaychamber.ca"
            >
              <TextInput
                value={senderDomainInput}
                onChange={setSenderDomainInput}
                placeholder="northbaychamber.ca"
              />
            </FieldLabel>
            <FieldLabel
              label="Email members should see"
              helper="Use an address on the same domain."
            >
              <TextInput
                value={senderDomainEmailInput}
                onChange={setSenderDomainEmailInput}
                placeholder="membership@northbaychamber.ca"
              />
            </FieldLabel>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSenderDomainModalOpen(false)}
                className={settingsSecondaryButtonClassName}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSenderDomainSetup}
                disabled={senderDomainActionLoading}
                className={settingsPrimaryButtonClassName}
              >
                {senderDomainActionLoading ? "Starting..." : "Start Email Setup"}
              </button>
            </div>
          </div>
        </SettingsModal>
      )}

      {testEmailModalOpen && (
        <SettingsModal
          title="Send test email"
          description="Send yourself a quick email to confirm everything looks right."
          onClose={() => {
            setTestEmailModalOpen(false);
            setTestEmailRecipient("");
          }}
        >
          <div className="space-y-4">
            <FieldLabel label="Recipient email">
              <TextInput
                value={testEmailRecipient}
                onChange={setTestEmailRecipient}
                placeholder="person@example.com"
              />
            </FieldLabel>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setTestEmailModalOpen(false);
                  setTestEmailRecipient("");
                }}
                className={settingsSecondaryButtonClassName}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendTestEmail}
                disabled={senderDomainTestEmailLoading}
                className={settingsPrimaryButtonClassName}
              >
                {senderDomainTestEmailLoading ? "Sending..." : "Send Test"}
              </button>
            </div>
          </div>
        </SettingsModal>
      )}
    </section>
  );
};

export default EmailDomainSettingsSection;
